import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getQuoteSwap } from '@/app/shared/services/api';
import { QuoteSwapRequest, QuoteSwapResponse, QuoteItem } from '@/app/shared/services/api/types';

import { DEFAULT_PROTOCOL, DEFAULT_PROTOCOL_CONFIG } from './constants';
import { normalizePermit2Eip712, normalizeTx, NormalizedQuote } from './helpers';

export type QuoteErrorType = 'quote_not_found' | 'service_unavailable' | 'insufficient_liquidity' | null;

export type UseSwapQuoteResult = {
  quote: NormalizedQuote | null;
  raw: QuoteSwapResponse | null;
  error: QuoteErrorType;
};

interface UseSwapQuoteParams {
  chain: string;
  from: string;
  to: string;
  amount: string;
  userAddress: string;
  slippage: string;
}

export const isValidSlippageForQuote = (slippage: string): boolean => {
  if (!slippage || slippage.endsWith('.')) return false;
  const value = Number(slippage);
  return Number.isFinite(value) && value > 0 && value <= 100;
};

const normalizeQuoteItem = (q: QuoteItem): NormalizedQuote => {
  const estimatedGas = q.estimatedGas ? Number(q.estimatedGas) : undefined;
  const rawQuote = q.rawQuote as Record<string, unknown>;
  return {
    protocol: q.protocol,
    amountReturned: q.amountReturned,
    tokenApprovalAddress: q.tokenApprovalAddress,
    logo: q.logo ?? DEFAULT_PROTOCOL_CONFIG.logo,
    estimatedGas,
    tx: normalizeTx(rawQuote),
    isSignatureNeededForSwap: Boolean(q.isSignatureNeededForSwap),
    permit2Eip712: normalizePermit2Eip712(rawQuote),
  };
};

export const useSwapQuote = (params: UseSwapQuoteParams) => {
  const isEnabled =
    !!params.userAddress &&
    !!params.from &&
    !!params.to &&
    !!params.amount &&
    params.amount !== '0' &&
    isValidSlippageForQuote(params.slippage);
  const { data: rawResponse, isFetching, isPending, dataUpdatedAt } = useQuery({
    queryKey: ['swapQuote', params.chain, params.from, params.to, params.amount, params.slippage],
    queryFn: async () => {
      return getQuoteSwap({
        ...params,
        protocol: DEFAULT_PROTOCOL,
      } as QuoteSwapRequest);
    },
    enabled: isEnabled,
    staleTime: 15_000,
    refetchInterval: 15_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });
  const data = useMemo<UseSwapQuoteResult>(() => {
    if (!rawResponse?.quote) {
      return {
        quote: null,
        raw: null,
        error: isEnabled ? 'quote_not_found' : null,
      };
    }
    return {
      quote: normalizeQuoteItem(rawResponse.quote),
      raw: rawResponse,
      error: null,
    };
  }, [rawResponse, isEnabled]);
  return { data, isFetching, isPending, dataUpdatedAt };
};
